/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import {
  clearProfessional,
  selectProfessional,
} from 'actions/SelectedProfessionalActions';
import {
  clearServiceCategory,
  selectServiceCategory,
} from 'actions/SelectedServiceCategoryActions';
import { clearServices } from 'actions/SelectedServicesActions';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { getAllProfessionals, hasTeamMembers } from 'selectors/professionals';
import { getAllServiceCategories } from 'selectors/services';

import Button from 'components/Button';
import { Select } from 'components/Select';

import { useAppContext } from 'hooks/context/useAppContext';
import { useGGTheme } from 'hooks/theme/useGGTheme';

import { ServicesError } from 'routes/services/ServicesError/ServicesError';

import { TProfessional } from 'types/appointment';

import s from './BookingFlow.module.css';

const toProfessionalOption = (item: TProfessional) => ({
  value: item.guid,
  label: item.full_name,
});

const toCategoryOption = (category: string) => ({
  value: category,
  label: category,
});

const allCategoriesOption = { label: 'View All', value: 'View All' };

export const BookingFlow = () => {
  const {
    components: { Layout },
  } = useGGTheme();

  const router = useRouter();
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const isMultiUser = useSelector(hasTeamMembers);
  const professionals = useSelector(getAllProfessionals);
  const serviceCategories = useSelector(getAllServiceCategories);
  const { publicUser } = useAppContext();

  const handleProfessionalSelection = (professionalGuid: string) => {
    const professional = professionals.find((p) => p.guid === professionalGuid);

    dispatch(selectProfessional(professional));
    dispatch(clearServiceCategory());
    dispatch(clearServices());
    void router.push('/services');
  };

  const handleServiceCategorySelection = (category: string) => {
    dispatch(selectServiceCategory(category));
    dispatch(clearProfessional());
    dispatch(clearServices());
    void router.push('/services');
  };

  useEffect(() => {
    if (!isMultiUser) {
      void router.replace('/services');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiUser]);

  if (publicUser.services_error) {
    return (
      <Layout>
        <ServicesError />
      </Layout>
    );
  }

  return (
    <Layout>
      <main className={s.root}>
        <h1 className={s.title}>Choose an option</h1>
        <form className={s.options}>
          <Select
            id="professional-dropdown"
            className={s.dropdown}
            name="professional"
            label="Select a Professional"
            options={professionals.map(toProfessionalOption)}
            onChange={(option: any) => {
              handleProfessionalSelection(option.value);
            }}
          />
          <span>OR</span>
          {serviceCategories?.length > 0 ? (
            <Select
              id="category-dropdown"
              name="category"
              label="Select a Service Category"
              className={s.dropdown}
              options={[
                allCategoriesOption,
                ...serviceCategories.map(toCategoryOption),
              ]}
              onChange={(option: any) => {
                handleServiceCategorySelection(option.value);
              }}
            />
          ) : (
            <Button to="/services">Choose a Service</Button>
          )}
        </form>
      </main>
    </Layout>
  );
};
